// File: src/pages/CostCards.js

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import config from '../config/environment'
import {
  PAGE_SIZE,
  readListCache,
  fetchCostCardsList
} from '../utils/costCardsList'
import './CostCards.css'

const SEARCH_DEBOUNCE_MS = 300

function Toast({ message, onClose }) {
  useEffect(() => {
    if (!message) return
    const t = setTimeout(onClose, 2500)
    return () => clearTimeout(t)
  }, [message, onClose])
  if (!message) return null
  return <div className="toast">{message}</div>
}

function SkeletonRows({ count = 8 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, idx) => (
        <tr key={`skeleton-${idx}`} className="skeleton-row">
          <td><div className="skeleton-cell skeleton-cell--short" /></td>
          <td><div className="skeleton-cell" /></td>
          <td><div className="skeleton-cell skeleton-cell--medium" /></td>
          <td><div className="skeleton-cell skeleton-cell--medium" /></td>
          <td><div className="skeleton-cell skeleton-cell--medium" /></td>
          <td><div className="skeleton-cell skeleton-cell--medium" /></td>
          <td><div className="skeleton-cell skeleton-cell--short" /></td>
        </tr>
      ))}
    </>
  )
}

export default function CostCards() {
  const [cards, setCards] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [sort, setSort] = useState('az')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [showModal, setShowModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [toast, setToast] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchRef = useRef()
  const debounceRef = useRef(null)
  const abortRef = useRef(null)
  const productFromUrl = searchParams.get('product') || ''

  useEffect(() => {
    if (productFromUrl) {
      setSearch(productFromUrl)
      setDebouncedSearch(productFromUrl)
    }
  }, [productFromUrl])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [search])

  useEffect(() => {
    const params = {
      page,
      limit: PAGE_SIZE,
      sort,
      search: debouncedSearch.trim(),
      product: productFromUrl && !debouncedSearch.trim() ? productFromUrl : ''
    }

    const cached = readListCache(params)
    if (cached) {
      setCards(cached.data)
      setTotal(cached.total)
      setTotalPages(cached.totalPages)
      setLoading(false)
      setRefreshing(true)
    } else {
      setLoading(true)
    }
    setError(null)

    if (abortRef.current) abortRef.current.abort()
    const controller = new AbortController()
    abortRef.current = controller

    fetchCostCardsList(params, { signal: controller.signal })
      .then(result => {
        if (controller.signal.aborted) return
        setCards(result.data)
        setTotal(result.total)
        setTotalPages(result.totalPages)
      })
      .catch(err => {
        if (controller.signal.aborted || err.name === 'AbortError') return
        if (!cached) setError(err.message)
      })
      .finally(() => {
        if (controller.signal.aborted) return
        setLoading(false)
        setRefreshing(false)
      })

    return () => controller.abort()
  }, [page, sort, debouncedSearch, productFromUrl])

  const fmtNum = v => {
    const n = Number(v)
    if (!isFinite(n)) return '—'
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(n)
  }

  const fmtDate = c => {
    const raw = c.periodExact || c.createdAt
    if (!raw) return '—'
    const d = new Date(raw)
    if (isNaN(d.getTime())) return '—'
    const dd = String(d.getUTCDate()).padStart(2, '0')
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const yy = d.getUTCFullYear()
    return `${dd}-${mm}-${yy}`
  }

  function openDeleteModal(id) {
    setDeleteId(id)
    setShowModal(true)
  }

  function closeDeleteModal() {
    setShowModal(false)
    setDeleteId(null)
  }

  async function confirmDelete() {
    if (!deleteId) return
    setDeletingId(deleteId)
    const base = config.api.baseUrl.replace(/\/$/, '')
    const endpoint = `${base}/api/costcards/${deleteId}`
    try {
      const res = await fetch(endpoint, { method: 'DELETE' })
      if (!res.ok) throw new Error(`Delete failed (${res.status})`)
      setCards(cs => cs.filter(c => c._id !== deleteId))
      setTotal(prev => Math.max(0, prev - 1))
      setToast('Deleted successfully')
    } catch (err) {
      setToast('Delete failed: ' + err.message)
    } finally {
      setDeletingId(null)
      closeDeleteModal()
    }
  }

  const handleSortChange = e => {
    setSort(e.target.value)
    setPage(1)
  }

  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const showingTo = Math.min(page * PAGE_SIZE, total)

  return (
    <div className="cost-cards-page">
      <div className="cost-cards-container">
        <Toast message={toast} onClose={() => setToast('')} />

        <h1>Cost Cards</h1>
        <button
          className="create-btn"
          onClick={() => navigate('/costcards/new')}
        >
          Create Cost Card
        </button>

        <div className="cost-cards-controls">
          <span className="item-count">
            {loading && cards.length === 0
              ? 'Loading...'
              : `${showingFrom}-${showingTo} of ${total} cards${refreshing ? ' (updating...)' : ''}`}
          </span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search cost cards..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 4, border: '1px solid #ccc' }}
          />
          <div className="sort-controls">
            <label htmlFor="sort-cards">Sort by:</label>
            <select
              id="sort-cards"
              value={sort}
              onChange={handleSortChange}
              style={{ padding: '4px 8px', borderRadius: 4, marginLeft: '8px' }}
            >
              <option value="az">Product Name (A-Z)</option>
              <option value="za">Product Name (Z-A)</option>
              <option value="period-newest">Period (Newest)</option>
              <option value="period-oldest">Period (Oldest)</option>
            </select>
          </div>
        </div>

        {error ? (
          <p className="error">Error: {error}</p>
        ) : (
          <>
            <div className="cost-cards-table-container">
              <table className="cost-cards-table">
                <thead>
                  <tr>
                    <th style={{ width: 40, position: 'sticky', left: 0, background: '#fff', zIndex: 2 }}>#</th>
                    <th>Product</th>
                    <th style={{ minWidth: 120 }}>Date</th>
                    <th>Total Cost (Kes/Kg)</th>
                    <th>Selling Price (Kes/Kg)</th>
                    <th>
                      Gross Profit (%)
                      <span className="info-tooltip" title="Gross profit margin as a percentage">&#9432;</span>
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && cards.length === 0 ? (
                    <SkeletonRows />
                  ) : cards.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="empty-state-cell">
                        No cost cards found. Click <b>Create Cost Card</b> to begin.
                      </td>
                    </tr>
                  ) : (
                    cards.map((c, idx) => {
                      const rowNum = (page - 1) * PAGE_SIZE + idx + 1
                      const isDeleting = deletingId === c._id
                      return (
                        <tr key={c._id} className={isDeleting ? 'row-deleting' : ''}>
                          <td>{rowNum}</td>
                          <td>{c.productName}</td>
                          <td>{fmtDate(c)}</td>
                          <td>{fmtNum(c.totalCostsKesPerKg)}</td>
                          <td>{fmtNum(c.sellingPriceKesPerKg)}</td>
                          <td>{fmtNum(c.grossProfitMarginPercent)}</td>
                          <td>
                            <button
                              className="icon-btn edit-btn"
                              title="Edit"
                              aria-label="Edit"
                              disabled={isDeleting}
                              onClick={() => {
                                const url = `/costcards/create/${c.templateKey}?id=${encodeURIComponent(c._id)}`
                                navigate(url, { state: { card: c } })
                              }}
                            >
                              <span className="icon-edit" />
                            </button>
                            <button
                              className="icon-btn delete-btn"
                              title="Delete"
                              aria-label="Delete"
                              disabled={isDeleting}
                              onClick={() => openDeleteModal(c._id)}
                            >
                              <span className="icon-delete" />
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!loading && totalPages > 1 && (
              <div className="cost-cards-pagination">
                <button
                  onClick={() => setPage(prev => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span className="cost-cards-pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Confirm Delete</h3>
              <p>Are you sure you want to delete this cost card?</p>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={closeDeleteModal}>Cancel</button>
                <button className="delete-btn" onClick={confirmDelete} disabled={!!deletingId}>
                  {deletingId ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
